import { useState, useRef, useEffect } from 'react';
import { authService, User } from '../utils/auth.ts';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import Avatar from './Avatar.tsx';
import '../styles/colors.css';

interface HamburgerMenuProps {
  user: User | null;
  onProfileEdit: () => void;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  user,
  onProfileEdit,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    authService.logout();
    window.location.reload();
  };

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      {/* ハンバーガーボタン */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          color: 'var(--neutral-50)',
          border: 'none',
          borderRadius: 'var(--radius-lg)',
          padding: '0.5rem',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          minWidth: 'fit-content',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
        }}
      >
        <Avatar size="small" style={{ marginRight: '0.5rem' }} />
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100px',
          }}
        >
          {user?.username}
        </span>
        <MenuIcon style={{ fontSize: '1.2rem', flexShrink: 0 }} />
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '0.5rem',
            backgroundColor: 'var(--bg-card)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-xl)',
            border: '1px solid var(--border-light)',
            minWidth: '180px',
            maxWidth: '90vw',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* プロフィール編集 */}
          <button
            onClick={() => {
              onProfileEdit();
              setIsOpen(false);
            }}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: '0.75rem 1rem',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: 'var(--text-primary)',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--neutral-100)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <PersonIcon
              style={{
                fontSize: '1.1rem',
                color: 'var(--primary-600)',
              }}
            />
            プロフィール編集
          </button>

          {/* 区切り線 */}
          <div
            style={{
              height: '1px',
              backgroundColor: 'var(--neutral-200)',
              margin: '0.25rem 0',
            }}
          />

          {/* ログアウト */}
          <button
            onClick={() => {
              handleLogout();
              setIsOpen(false);
            }}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: '0.75rem 1rem',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: 'var(--accent-rose)',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--primary-50)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <LogoutIcon style={{ fontSize: '1.1rem' }} />
            ログアウト
          </button>
        </div>
      )}
    </div>
  );
};

export default HamburgerMenu;
